// Wall-clock <-> Europe/London-offset ISO helpers — fire-check time tracking
// (instance.time_started/time_ended) always stores an explicit UK offset
// (+00:00 GMT or +01:00 BST) rather than a naive/UTC string, so it reads back
// unambiguously everywhere (this app, the PDF/Word export).

const LONDON_FMT = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
});

function partsOf(date) {
    return Object.fromEntries(LONDON_FMT.formatToParts(date).map((p) => [p.type, p.value]));
}

// Minutes London is ahead of UTC for the instant `date` represents (0 = GMT, 60 = BST).
function londonOffsetMinutes(date) {
    const p = partsOf(date);
    const asLondonUtcMs = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    return Math.round((asLondonUtcMs - date.getTime()) / 60000);
}

function offsetSuffix(mins) {
    const sign = mins >= 0 ? "+" : "-";
    const abs = Math.abs(mins);
    return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}

// Current instant as an ISO string carrying the correct London offset.
export function nowLondonIso() {
    const now = new Date();
    const p = partsOf(now);
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}${offsetSuffix(londonOffsetMinutes(now))}`;
}

// "HH:MM" of the current London wall-clock time.
export function nowLondonHHMM() {
    const p = partsOf(new Date());
    return `${p.hour}:${p.minute}`;
}

// "YYYY-MM-DDTHH:MM" (datetime-local, naive) wall-clock -> an ISO string with
// the correct London offset for THAT calendar date (handles BST/GMT either
// side of a DST change).
export function localInputToLondonIso(str) {
    if (!str) return "";
    const [datePart, timePart] = str.split("T");
    const [y, m, d] = datePart.split("-").map(Number);
    const [hh, mm] = (timePart || "00:00").split(":").map(Number);
    const probe = new Date(Date.UTC(y, m - 1, d, 12, 0, 0)); // noon UTC avoids day-boundary DST edge cases
    const offMin = londonOffsetMinutes(probe);
    return `${datePart}T${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00${offsetSuffix(offMin)}`;
}

// Any offset-aware ISO string -> "YYYY-MM-DDTHH:MM" of its London wall-clock,
// for populating a <input type="datetime-local">.
export function isoToLocalInput(iso) {
    if (!iso) return "";
    try {
        const p = partsOf(new Date(iso));
        return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
    } catch {
        return "";
    }
}
